<template>
  <v-row justify="center">
    <v-dialog v-model="dialog" scrollable max-width="600px">
      <v-card>
        <v-card-title class="grey--text text-h5">
          Visitor
        </v-card-title>

        <v-divider></v-divider>

        <v-card-text class="mb-0 pb-0">
          <v-form class="pt-7 pb-0" ref="form" v-model="valid" lazy-validation>
            <v-text-field 
              dense 
              v-model="visitor.guest_name" 
              label="Full Name" 
              :error-messages="apiErrors.guest_name"
              outlined 
              :rules="rules.required"
              :readonly="visitor?.id ? true : false"
            ></v-text-field>

            <!--  Phone Number -->
            <div class="pb-2">
              <template>
                <vue-phone-number-input 
                  v-model="phoneNumber" 
                  @update="updatePhoneNumber"
                  :default-country-code="national_number" 
                  size="md"
                  :readonly="visitor?.id ? true : false"
                ></vue-phone-number-input>
                <span class="text-caption pl-4 red--text">{{ apiErrors.phone ? apiErrors.phone[0] : '' }}</span>
                <span class="text-caption red--text" v-if="phoneValidationError">{{ phoneValidationError }}</span>
              </template>
            </div>

            <v-row>
              <!-- ID Number -->
              <v-col>
                <v-text-field 
                  dense 
                  v-model="visitor.id_number" 
                  label="National ID Number"
                  :error-messages="apiErrors.id_number" 
                  outlined
                  :rules="rules.id_number"
                  maxlength="20"
                  prepend-icon="mdi-card-account-details"
                  :readonly="visitor?.id ? true : false"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row>
              <!-- Company Name -->
              <v-col v-if="userOrganizations.length > 0">
                <v-select 
                  dense 
                  v-model="organization.id" 
                  :items="userOrganizations" 
                  label="Organization/Branch"
                  :error-messages="apiErrors.organization_id" 
                  item-text="legal_name" 
                  item-value="id" 
                  outlined
                  :rules="rules.select"
                  :auto-select-first="true"
                  @change="selectOrganization"
                  :readonly="user.is_admin == 1 || visitor?.id"
                ></v-select>
              </v-col>

              <!--  Location -->
              <v-col v-if="organizationFacilities.length > 0">
                <v-select 
                  dense 
                  v-model="visitor.facility_id" 
                  :items="organizationFacilities" 
                  label="Location"
                  :error-messages="apiErrors.facility_id" 
                  item-text="facility" 
                  item-value="id" 
                  outlined
                  :rules="rules.select"
                  :auto-select-first="true"
                  :readonly="visitor?.id ? true : false"
                ></v-select>
              </v-col>
            </v-row>

            <v-row>
              <!-- Approval Workflow -->
              <v-col v-if="approvalWorkflows.length > 0">
                <v-select 
                  dense 
                  v-model="visitor.workflow_id" 
                  :items="approvalWorkflows"
                  label="Workflow"
                  :error-messages="apiErrors.workflow_id"
                  item-text="title"
                  item-value="id" 
                  outlined
                  :rules="rules.select"
                  :auto-select-first="true"
                  :readonly="user.is_admin == 1 || visitor?.id"
                ></v-select>
              </v-col>
            </v-row>

            <v-row>
              <!-- Date -->
              <v-col>
                <v-menu :close-on-content-click="closeOnContentClick" max-width="290px" min-width="290px">
                  <template v-slot:activator="{ on: menu, attrs }">
                    <v-text-field 
                      dense 
                      v-model="dateToday" 
                      label="Date" 
                      v-bind="attrs" 
                      v-on="{ ...menu }"
                      :error-messages="apiErrors.event_date" 
                      outlined
                      :readonly="visitor?.id ? true : false"
                    ></v-text-field>
                  </template>
                  <v-date-picker v-model="dateToday" color="primary" header-color="primary"></v-date-picker>
                </v-menu>
              </v-col>

              <!-- From -->
              <v-col>
                <v-menu :close-on-content-click="closeOnContentClick" max-width="290px" min-width="290px">
                  <template v-slot:activator="{ on: menu, attrs }">
                    <v-text-field 
                      dense 
                      v-model="startTime" 
                      label="From" 
                      v-bind="attrs" 
                      v-on="{ ...menu }"
                      :error-messages="apiErrors.from_time" 
                      outlined
                      :readonly="visitor?.id ? true : false"
                    ></v-text-field>
                  </template>
                  <v-time-picker v-model="startTime" scrollable format="24hr"></v-time-picker>
                </v-menu>
              </v-col>

              <!-- To -->
              <v-col>
                <v-menu :close-on-content-click="closeOnContentClick" max-width="290px" min-width="290px">
                  <template v-slot:activator="{ on: menu, attrs }">
                    <v-text-field 
                      dense 
                      v-model="endTime" 
                      label="To" 
                      v-bind="attrs" 
                      v-on="{ ...menu }"
                      :error-messages="apiErrors.end_time" 
                      outlined
                      :readonly="visitor?.id ? true : false"
                    ></v-text-field>
                  </template>
                  <v-time-picker v-model="endTime" scrollable format="24hr"></v-time-picker>
                </v-menu>
              </v-col>
            </v-row>

            <v-select 
              dense 
              v-model="visitor.category" 
              :items="visitCategories" 
              label="Category"
              :error-messages="apiErrors.category" 
              outlined
            ></v-select>

            <v-autocomplete 
              v-if="organizationUsers.length > 0"
              dense 
              v-model="visitor.host_id" 
              :search-input.sync="search"
              :items="organizationUsers" 
              item-value="id"
              item-text="attributes.name" 
              label="Host" 
              :error-messages="apiErrors.host_id" 
              outlined
              autocomplete="off"
              :loading="loading"
              hide-no-data
              hide-selected
              :no-filter="true"
              :readonly="visitor?.id ? true : false"
            ></v-autocomplete>

            <v-text-field 
              dense 
              v-model="visitor.title" 
              label="Title" 
              :error-messages="apiErrors.title" 
              outlined
              :readonly="visitor?.id ? true : false"
            ></v-text-field>

            <v-textarea 
              dense 
              v-model="visitor.assets" 
              label="Assets" 
              :error-messages="apiErrors.assets" 
              outlined
              :readonly="visitor?.id ? true : false"
            ></v-textarea>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-btn 
            color="red darken-1 white--text"
            outlined 
            @click="closeModal" 
            class="text-capitalize mx-1"
          >
            Cancel <v-icon small>mdi-close</v-icon>
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn 
            color="green darken-1 white--text" 
            outlined
            @click="saveVisit" 
            class="text-capitalize mx-1"
            :loading="submitting"
            v-if="!visitor?.id"
          >
            Save <v-icon small>mdi-content-save-outline</v-icon>
          </v-btn>

          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <span class="d-inline-block mb-1" v-bind="attrs" v-on="on">
                <v-btn
                  color="warning darken-1"
                  outlined
                  class="text-capitalize mx-1"
                  dark
                  :loading="loading"
                  @click="rejectVisit"
                  v-if="approvalStatus(visitor)"
                >
                  Reject <v-icon small>mdi-cancel</v-icon>
                </v-btn>
              </span>
            </template>
            <span>Reject Visit</span>
          </v-tooltip>
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <span class="d-inline-block mb-1" v-bind="attrs" v-on="on">
                <v-btn
                  color="success"
                  outlined
                  dark
                  class="text-capitalize mx-1"
                  :loading="loading"
                  @click="approveVisit"
                  v-if="approvalStatus(visitor)"
                >
                  Approve <v-icon>mdi-content-save-outline</v-icon>
                </v-btn>
              </span>
            </template>
            <span>Approve Visit</span>
          </v-tooltip>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import Visitor from '../models/visitor';
import EventsService from '../services/events.service';
import OrganizationService from '../services/organization.service';

export default {
  props: {
    value: {
      type: Boolean,
      required: true
    },
    organizationFacilities: {
      type: Array,
      required: true
    },
    approvalWorkflows: {
      type: Array,
      required: false
    },
    organization: {
      type: Object,
      required: true
    },
    selectedDate: {
      type: String,
      required: false
    },
    host: {
      type: Object,
      required: false
    },
    selectedVisit: {
      type: Object,
      required: false
    },
  },
  computed: {
    dialog: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      }
    },
    user() {
      return JSON.parse(localStorage.getItem("user"));
    },
    userOrganizations() {
      return this.user?.organisations ?? [];
    },
  },
  created() {
    this.visitor = new Visitor();
    this.calculateTime();
  },
  data() {
    return {
      options: {
        page: 1,
        itemsPerPage: 10,
        filter: null
      },
      search: null,
      organizationUsers: [],
      visitor: new Visitor(),
      national_number: "KE",
      phoneNumber: null,
      phoneValidationError: null,
      dateToday: new Date().toISOString().substr(0, 10),
      startTime: null,
      endTime: null,
      visitCategories: [
        "Official", 
        "Personal",
        "Suppliers",
        "Commercial Bank Representatives",
        "Microfinance & Forex Bureau Officials",
        "Digital Financial Services & FinTech Providers",
        "On-Site Inspection Teams / Auditors",
        "M-Pesa / Mobile Money Operators",
        "National Treasury Officials",
        "Kenya Revenue Authority (KRA) Representatives",
        "State Attorneys & Law Enforcement",
        "Parastatal & State Corporation Executives",
        "Treasury Bills & Bonds Investors",
        "Primary Debt Dealers / Market Makers",
        "Foreign Exchange Dealers",
        "International Monetary Fund (IMF) & World Bank Delegates",
        "Regional Central Bank Delegations",
        "IT & ERP Infrastructure Vendors",
        "Currency & Minting Logistics Providers",
        "General Facility Procurement Vendors",
        "Job Candidates & Interviewees",
        "Academic Researchers & Interns",
        "VVIPs & Diplomatic Missions"
      ],
      apiErrors: {},
      valid: false,
      submitting: false,
      loading: false,
      closeOnContentClick: false,
      rules: {
        select: [(v) => !!v || "An item should be selected"],
        select2: [(v) => v.length > 0 || "Atleast one item should be selected"],
        required: [
          (v) => !!v || "Field is required",
          (v) => (v && v.length >= 3) || "Min 3 characters",
        ],
        file: [
          (v) => !!v || "File is required",
          (v) => (v && v.length > 0) || "File is required",
        ],
        id_number: [
          (v) => !!v || "ID Number is required",
          (v) => (v && v.length >= 6) || "Min 6 characters",
          (v) => (v && v.length <= 20) || "ID Number must be at most 20 characters"
        ],
      },
    }
  },
  watch: {
    dialog: {
      handler(value) {
        if (value) {
          this.getUsers();
          this.visitor.category = "Official";
          this.visitor.workflow_id = this.approvalWorkflows.length > 0 ? this.approvalWorkflows[0].id : null;
          this.calculateTime();
        }
      },
      deep: true
    },
    search: {
      handler(value) {
        this.getUsers(value);
      },
      deep: true
    },
    selectedDate: {
      handler(value) {
        this.dateToday = value;
      },
      deep: true
    },
    approvalWorkflows: {
      handler(newVal) {
        if (newVal && newVal.length > 0 && !this.visitor.workflow_id) {
          this.visitor.workflow_id = newVal[0].id;
        }
      },
      immediate: true
    },
    selectedVisit: {
      handler(newVal) {
        if (newVal) {
          this.visitor = { ...newVal };
          this.phoneNumber = newVal.guest_phone;
          this.dateToday = newVal.event_date;
          this.startTime = newVal.from_time;
          this.endTime = newVal.end_time;
          this.getUsers();
        } else {
          this.visitor = new Visitor();
          this.phoneNumber = null;
          this.dateToday = new Date().toISOString().substr(0, 10);
          this.calculateTime();
        }
      },
      immediate: true
    },
  },
  methods: {
    selectOrganization() {
      this.getUsers();
      this.$emit('select_organization', { selectedTabId: 4, selectedOrganization: this.organization });
    },
    calculateTime() {
      let time = new Date(Date.now());
      let hour = time.getHours();
      let minutes = time.getMinutes();

      if (minutes > 30) {
        hour += 2;
      } else {
        hour += 1;
      }

      this.startTime = hour + ":00";
      this.endTime = (hour + 1) + ":00";
    },
    closeModal() {
      this.phoneNumber = null;
      this.visitor = new Visitor();
      this.dialog = false;
    },
    saveVisit() {
      this.valid = this.$refs.form.validate();
      if (this.valid) {
        this.validatePhoneNumber();
        
        this.visitor.from_time = this.startTime;
        this.visitor.end_time = this.endTime;
        this.visitor.event_date = this.dateToday;

        this.submitting = true;

        EventsService.store(this.visitor).then(
          (response) => {
            if (response.status === 200) {
              this.$store.dispatch("alert/success", "Visitor invited");
              this.loading = false;
              this.submitting = false;
              this.$emit('get_upcoming_visits');
            } else {
              this.submitting = false;
              this.$store.dispatch("alert/error", (this.user.name ?? "Visitor") + " not invited" + response.message);
            }

            this.closeModal();
          },
          (error) => {
            this.submitting = false;

            if (error.response.status === 422) {
              this.apiErrors = error.response.data.errors
              console.log(error.response.data.errors)
              this.$store.dispatch("alert/error", error.response.data.message);
              this.loading = false;
            }

            this.$store.dispatch("alert/error", error.response.data.message);
          }
        );
      } else {
        this.$store.dispatch("alert/error", 'Form is Invalid!');
      }
    },
    index() {
      this.$emit('get_upcoming_visits');
    },
    updatePhoneNumber(data) {
      this.visitor.guest_phone = data.e164
    },
    validatePhoneNumber() {
      if (this.phoneNumber == null) {
        this.phoneErrorColor = "red";
        this.phoneValidationError = "The phone number is required";
      }
    },
    getUsers(search = null) {
      this.loading = true;
      if (!this.host?.id && !this.visitor?.id) {
        this.options.filter = search;
      } else if (this.visitor?.id) {
        this.options.filter = this.visitor?.host_name ?? null;
      }

      OrganizationService.organization_users(this.organization.id, { options: this.options }).then(
        success => {
          this.organizationUsers = success?.data?.data?.data ?? this.organizationUsers;
          this.loading = false;

          if (this.host?.id ?? this.visitor?.host_id) {
            let index = -1;
            index = this.organizationUsers.findIndex(user => user.id == (this.host?.id ?? this.visitor?.host_id));
            if (index > -1) {
              this.visitor.host_id = this.organizationUsers[index].id;
            }
          }
        },
        error => {
          console.log(error)
          this.organizationUsers = [];
        }
      )
    },
    approvalStatus(item) {
      if (!item || !item.id) {
        return false;
      }

      if (item.execute == 1 || item.active == 0) {
        return false;
      }

      return true;
    },

    approveVisit() {
      this.loading = true;
      this.valid = this.$refs.form.validate();
      this.visitor.sender_id = this.user.id;
      if (this.valid) {
        this.visitor.status += 1;
        EventsService.approve(this.visitor).then(
          (response) => {
            if (response.status == 200) {
              this.$store.dispatch("alert/success", response.data.message);
              this.loading = false; 

              this.index();
              this.closeModal();
            } else {
              this.$store.dispatch("alert/error", response.data.message);
              this.index();
              this.loading = false;
            }
          },
          (error) => {
            if (error.response.status == 422) {
              this.$store.dispatch("alert/error", error.response.data.message);
              this.loading = false;
              this.index();
            }

            if (error.response.status == 401) {
              this.handleLogout();
              this.loading = false;
              this.index();
            }

            this.$store.dispatch("alert/error", error.response.data.message);
          }
        );
      } else {
        this.loading = false;
        this.index();
      }
    },

    rejectVisit() {
      this.loading = true;
      this.valid = this.validate();
      this.visitor.sender_id = this.user.id;
      if (this.valid) {
        this.visitor.status -= 1;
        EventsService.reject(this.visitor).then(
          (response) => {
            if (response.status == 200) {
              this.$store.dispatch("alert/success", response.data.message);
              this.loading = false;
              this.index();
              this.closeModal();
            } else {
              this.$store.dispatch("alert/error", response.data.message);
              this.index();
              this.loading = false;
            }
          },
          (error) => {
            if (error.response.status == 422) {
              this.$store.dispatch("alert/error", error.response.data.message);
              this.loading = false;
              this.index();
            }
            if (error.response.status == 401) {
              this.handleLogout();
              this.loading = false;
              this.index();
            }
            
            this.$store.dispatch("alert/error", error.response.data.message);
          }
        );
      } else {
        this.loading = false;
      }
    },
  },
}
</script>